import type React from "react";
import { useEffect, useId, useState } from "react";
import config from "../../../config";
import { fetchAuthenticatedBlobUrl, s3DownloadUrl } from "../../../helpers/authenticatedDownload";
import AuthenticatedDownloadLink from "../../app/AuthenticatedDownloadLink";
import FileDropzone from "../../app/FileDropzone";
import Loading from "../../app/Loading";
import { FieldMessages, fieldAccessibility, type LegacyField } from "./LegacyFieldAdapter";

// Redux Form adapter for file selection, extracted from inputs.jsx as part of
// establishing FileDropzone as the sole owner of react-dropzone. inputs.jsx
// re-exports this as `DropzoneInput` so existing call sites are unchanged.
//
// The adapter owns every Redux Form concern — reading field.input, translating
// the selection back into field.input.onChange, rendering the label and the
// messages — plus the two application concerns the plan keeps out of the owned
// component: previewing and downloading an already-stored document.

type DropzoneFieldProps = LegacyField & {
  disabled?: boolean;
  downloadButton?: boolean;
  downloadLabel?: string;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onChange: (files: File[]) => void;
    value?: readonly File[] | string | null;
  };
  lastUploadDate?: string;
  multiple?: boolean;
  placeholder?: React.ReactNode;
  preview?: boolean;
  uploading?: boolean;
};

/** A selection in progress, an absent value, or the string "null" — nothing stored. */
function emptyPath(path: readonly File[] | string | null | undefined): boolean {
  return !path || path === "" || path === "null" || Array.isArray(path);
}

function DownloadLink({
  label,
  lastUploadDate,
  path,
}: {
  label?: string;
  lastUploadDate?: string;
  path: readonly File[] | string | null | undefined;
}): React.ReactElement | null {
  if (emptyPath(path)) {
    return null;
  }
  return (
    <p>
      <AuthenticatedDownloadLink className="btn-download" url={s3DownloadUrl(String(path))}>
        {label || "Download file"}
      </AuthenticatedDownloadLink>
      {lastUploadDate && <> uploaded on {lastUploadDate}</>}
    </p>
  );
}

function Preview({ path }: { path: readonly File[] | string | null | undefined }): React.ReactElement | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (emptyPath(path)) return;
    let active = true;
    let objectUrl: string | undefined;
    const [type, name, ...fileParts] = String(path).split("/");
    const fileQuery = fileParts.length > 0 ? `?file=${encodeURIComponent(fileParts.join("/"))}` : "";
    const imageUrl = `${config.api}/documents/images/show/${encodeURIComponent(type)}/${encodeURIComponent(name)}${fileQuery}`;
    fetchAuthenticatedBlobUrl(imageUrl)
      .then((result) => {
        objectUrl = result.url;
        if (!active) {
          URL.revokeObjectURL(result.url);
          return;
        }
        setUrl(result.url);
      })
      .catch(() => {
        if (active) setUrl(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  if (emptyPath(path)) {
    return null;
  }
  return url ? <img className="img-fluid" alt="" src={url} /> : <Loading label="Loading preview..." />;
}

export default function DropzoneField(field: DropzoneFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const files = field.input.value;
  // A missing placeholder still rendered an empty label before the extraction,
  // so it is coerced rather than dropped.
  const label = field.hideLabel ? undefined : (field.placeholder ?? "");

  return (
    <FileDropzone
      aria-describedby={accessibility.inputProps["aria-describedby"]}
      aria-invalid={accessibility.inputProps["aria-invalid"]}
      aria-required={accessibility.inputProps["aria-required"]}
      buttonLabel={field.uploading ? <Loading label="Uploading..." /> : <span>Upload</span>}
      className={accessibility.hasError ? "app-validation-error dropzone-wrapper" : "dropzone-wrapper"}
      disabled={field.disabled}
      id={inputId}
      label={label}
      multiple={field.multiple || false}
      onSelect={field.input.onChange}
      value={files}
    >
      <FieldMessages accessibility={accessibility} field={field} />
      {field.downloadButton && (
        <DownloadLink label={field.downloadLabel} lastUploadDate={field.lastUploadDate} path={files} />
      )}
      {field.preview && <Preview path={files} />}
    </FileDropzone>
  );
}
